<!-- Component for editing 2D drawing name -->
<template>
	<div class="img-name ml-2">
		<ListItemLabel v-if="!isRenamingMode" :text="image?.name ?? ''" icon="mdi-drawing-box" />
		<div class="img-name-input" v-else>
			<VeeValidateForm :initial-values="{ name: image?.name }" v-slot="{ meta }">
				<VeeValidateField name="name" :rules="nameRules" v-slot="{ field, errorMessage }">
					<ErrorTooltip placement="bottom" :shown="!meta.valid" :triggers="[]">
						<v-text-field v-model="name" v-bind="field" type="text" :error="!meta.valid" color="primary"
							class="ma-0 pa-0" hide-details="" variant="outlined" autofocus style="font-size: 14px"
							:loading="isLoading" @blur="blur(meta.valid)" @keydown.enter.prevent="onEnterKeydown($event, meta.valid)"
							@keyup.escape="cancelUpdateFileName">
						</v-text-field>
						<template #popper>
							<span>{{ errorMessage }}</span>
						</template>
					</ErrorTooltip>
				</VeeValidateField>
			</VeeValidateForm>
		</div>
	</div>
</template>

<script>
import { isImeComposingKeyEvent } from "@/utils/imeComposition";
import ListItemLabel from "@/components/Project/ListItemLabel.vue";
import ErrorTooltip from "@/components/common/ErrorTooltip";
import axios from "axios";
import {
	Field as VeeValidateField,
	Form as VeeValidateForm,
} from "vee-validate";
import { mapMutations } from "vuex";

export default {
	components: {
		VeeValidateForm,
		VeeValidateField,
		ErrorTooltip,
		ListItemLabel,
	},
	props: {
		imageId: String,
		isRenamingMode: Boolean,
	},
	data() {
		return {
			name: "",
			hasUpdatedError: false,
			nameRules: [
				(value) => {
					if (value) return true;
					return this.$t("REQUIRED_ERROR");
				},
				(value) => {
					if (value?.length <= 100) return true;
					return this.$t("MAX_LENGTH_ERROR");
				},
			],
		};
	},
	computed: {
		isLoading: {
			get() {
				return this.$store.state.isLoading;
			},
			set(value) {
				this.$store.dispatch("updateIsLoading", value);
			},
		},
		image() {
			const list = this.$store.state.drawing2dImage.drawing2dImageList;
			return list.find((item) => String(item.assetId) === String(this.imageId));
		},
	},
	watch: {
		name() {
			if (this.hasUpdatedError) this.hasUpdatedError = false;
		},
	},
	methods: {
		...mapMutations(["set_snackbar"]),
		async blur(valid) {
			if (this.isLoading) return;
			if (this.hasUpdatedError) {
				this.cancelUpdateFileName();
				return;
			}
			if (!valid) {
				this.cancelUpdateFileName();
				return;
			}
			if (this.name === this.image.name) {
				this.endNameEditing();
				return;
			}
			// Show spinner and send request
			await this.$store.dispatch("executeWithSpinner", async () => {
				// End editing if request succeeds, revert changes if it fails
				const status = await this.updateFileName();
				if (status) {
					this.endNameEditing();
				} else {
					this.cancelUpdateFileName();
				}
			});
		},
		/** Confirm the name on Enter, ignoring the one that confirms IME composition. */
		onEnterKeydown(e, valid) {
			if (isImeComposingKeyEvent(e)) return;
			this.enter(valid);
		},
		async enter(valid) {
			if (this.isLoading) return;
			if (!valid) {
				return;
			}
			if (this.name === this.image.name) {
				this.endNameEditing();
				return;
			}
			// Show spinner and send request
			await this.$store.dispatch("executeWithSpinner", async () => {
				// End editing if request succeeds
				const status = await this.updateFileName();
				if (status) {
					this.endNameEditing();
				}
			});
		},
		async updateFileName() {
			this.image.name = this.name;
			const status = await this.request();
			return status;
		},
		cancelUpdateFileName() {
			this.name = this.image.name;
			this.endNameEditing();
		},
		endNameEditing() {
			this.hasUpdatedError = false;
			this.image.name = this.name;
			this.$emit("end-rename");
		},
		async request() {
			const successMessage = `${this.$t("UPDATE_DATA")} ${this.$t("successful")}`;
			const errorMessage = `${this.$t("UPDATE_DATA")} ${this.$t("failed")}`;
			try {
				this.isLoading = true;
				// Send update request
				const res = await axios.put(
					`${import.meta.env.VITE_API_BASE}/drawing2dImage/setting/${this.$route.query.siteId}/${this.imageId}`,
					{ name: this.name },
				);
				if (res.status === 200) {
					// Refresh drawing list
					await this.$store.dispatch(
						"drawing2dImage/getDrawing2dImageList",
						this.$route.query.siteId,
					);
					this.set_snackbar({
						text: successMessage,
						color: "rgba(0, 153, 0, 0.72)",
					});
					return res.status;
				} else {
					throw new Error(errorMessage);
				}
			} catch {
				this.hasUpdatedError = true;
				this.set_snackbar({
					text: errorMessage,
					color: "rgba(153, 0, 0, 0.72)",
				});
				return "";
			}
		},
	},
	created() {
		this.name = this.image?.name;
	},
};
</script>

<style lang="scss" scoped>
.img-name {
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;

	:deep(input.v-field__input) {
		padding: 0 6px;
		min-height: 0px;
	}
}

.img-name-input {
	width: 100%;
}
</style>
